// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { arcRows, sameNumber } from '../../../utils/arcRows';
import { ArcField, ArcRow, LanguagePair } from '../../../utils/arcRows';
import { describeError as describeApiError, serverMessages } from '../../../utils/serverErrors';
import { ApiError, ApiErrorEntry, SaveFailure } from '../../../utils/serverErrors';
import { LatestChangeSet } from '../ChangeSets/changeSets';

export const TER_GROUPS_ROUTE = '/settings/terGroups';
export const TER_GROUPS_PATH = '/terGroups';

export const terGroupRoute = (terGroup: string): string =>
  `${TER_GROUPS_ROUTE}/${encodeURIComponent(terGroup)}`;

export const terGroupPath = (terGroup: string): string =>
  `${TER_GROUPS_PATH}/${encodeURIComponent(terGroup)}`;

export const READ_ONLY_NOTE = 'You can view TER groups but not change them.';

const WRITE_DENIED_MESSAGE = 'You do not have permission to change TER groups.';

export interface TerGroupSummary {
  terGroup: string;
  groupName: string;
  workflow?: string | null;
  contentType?: string | null;
  client?: string | null;
  // The TERs of arcs the group does not list; null for a group made before there were two, until
  // both are set.
  bilingualDefaultTer: number | null;
  monolingualDefaultTer: number | null;
  version: number;
  author: string;
  // ISO-8601 UTC timestamp of when this version was created.
  createdAt?: string;
}

export interface Ter {
  languagePair: LanguagePair;
  ter: number;
}

export interface TerGroup extends TerGroupSummary, LatestChangeSet {
  ters: Ter[];
}

export type MetadataField = 'workflow' | 'contentType' | 'client';

export const METADATA_FIELDS: { field: MetadataField; label: string }[] = [
  { field: 'workflow', label: 'Workflow' },
  { field: 'contentType', label: 'Content type' },
  { field: 'client', label: 'Client' },
];

export type DefaultTerField = 'bilingualDefaultTer' | 'monolingualDefaultTer';

/** The two default TERs, each for the arcs of its kind that the group does not list. */
export const DEFAULT_TERS: { field: DefaultTerField; label: string; description: string }[] = [
  {
    field: 'bilingualDefaultTer',
    label: 'Default TER (bilingual)',
    description: 'For an arc the group does not list, unless it is monolingual.',
  },
  {
    field: 'monolingualDefaultTer',
    label: 'Default TER (monolingual)',
    description:
      'For an arc the group does not list whose source and target are the same locale, or both English (en-* to en-*).',
  },
];

/** The fields of a TER group besides its name and TERs, as entered. */
export type Metadata = Record<MetadataField | DefaultTerField, string>;

export const emptyMetadata = (): Metadata => ({
  workflow: '',
  contentType: '',
  client: '',
  bilingualDefaultTer: '',
  monolingualDefaultTer: '',
});

const terText = (ter: number | null | undefined): string =>
  ter === null || ter === undefined ? '' : String(ter);

export const metadataOf = (group: TerGroupSummary): Metadata => ({
  workflow: group.workflow || '',
  contentType: group.contentType || '',
  client: group.client || '',
  bilingualDefaultTer: terText(group.bilingualDefaultTer),
  monolingualDefaultTer: terText(group.monolingualDefaultTer),
});

/**
 * Whether either entered default TER differs from the group's; the only details that can change.
 * Entering a default the group does not have is a change.
 */
export const defaultTerChanged = (metadata: Metadata, group: TerGroupSummary): boolean => {
  const loaded = metadataOf(group);
  return DEFAULT_TERS.some(({ field }) =>
    loaded[field] === ''
      ? metadata[field].trim() !== ''
      : !sameNumber(metadata[field], loaded[field])
  );
};

/** Whether the group lacks either default TER, as one made before there were two does. */
export const lacksDefaultTers = (group: TerGroupSummary): boolean =>
  DEFAULT_TERS.some(({ field }) => group[field] === null || group[field] === undefined);

/** Why a group without both default TERs cannot weight arcs by TER; Pricing refuses to use it. */
export const missingDefaultTersMessage = (group: TerGroupSummary): string =>
  `TER group ${group.groupName} is missing its bilingual or monolingual default TER. Set both before a scoping model can use it.`;

export const WORKFLOW_REQUIRED_MESSAGE = 'Enter a workflow.';

/** A new group needs a workflow; blank or only spaces does not count. */
export const workflowValid = (value: string): boolean => value.trim() !== '';

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;
const MAX_DECIMALS = 3;

/** A TER, per arc or default, is a rate from 0 to 1 with at most 3 decimal places. */
export const validateTer = (value: string): string | undefined => {
  const trimmed = value.trim();
  const ter = Number(trimmed);
  if (!DECIMAL_PATTERN.test(trimmed) || ter > 1) {
    return 'Enter a number from 0 to 1.';
  }
  if ((trimmed.split('.')[1] || '').length > MAX_DECIMALS) {
    return `Use at most ${MAX_DECIMALS} decimal places.`;
  }
  return undefined;
};

export const DEFAULT_TER_CONSTRAINT = 'Required. A number from 0 to 1, up to 3 decimal places.';

/** The error for an entered default TER; none while it is empty, though saving still needs one. */
export const defaultTerError = (value: string): string | undefined =>
  value.trim() === '' ? undefined : validateTer(value);

/** Whether a default TER is entered and valid. */
export const defaultTerValid = (value: string): boolean => validateTer(value) === undefined;

/** Whether both default TERs are entered and valid. */
export const defaultTersValid = (metadata: Metadata): boolean =>
  DEFAULT_TERS.every(({ field }) => defaultTerValid(metadata[field]));

/**
 * Whether an arc takes the monolingual default TER when the group does not list it: both locales
 * are named and are the same locale, or both English. Mirrors Pricing's
 * `WeightSchemeGenerator.isMonolingual`.
 */
export const isMonolingual = ({ sourceLocale, targetLocale }: LanguagePair): boolean => {
  if (sourceLocale === null || targetLocale === null) {
    return false;
  }
  const isEnglish = (locale: string): boolean => locale.split('-')[0].toLowerCase() === 'en';
  return (
    sourceLocale.toLowerCase() === targetLocale.toLowerCase() ||
    (isEnglish(sourceLocale) && isEnglish(targetLocale))
  );
};

/** The group's default TER for an arc it does not list; null when the group lacks it. */
export const defaultTerOf = (group: TerGroupSummary, languagePair: LanguagePair): number | null =>
  isMonolingual(languagePair) ? group.monolingualDefaultTer : group.bilingualDefaultTer;

export const terRows = arcRows<Ter, 'ter'>({
  columns: [
    {
      key: 'ter',
      header: 'TER',
      numeric: true,
      step: 0.001,
      same: sameNumber,
      validate: validateTer,
    },
  ],
  valuesOf: ({ ter }) => ({ ter: String(ter) }),
  itemOf: (languagePair, { ter }) => ({ languagePair, ter: Number(ter) }),
  collection: 'ters',
  rejectedMessage: 'Pricing rejected the TER group.',
  accessDeniedMessage: WRITE_DENIED_MESSAGE,
});

export type TerRow = ArcRow<'ter'>;

type RowField = ArcField<'ter'>;

export interface TerGroupCreateRequest
  extends Partial<Record<MetadataField, string>>,
    Record<DefaultTerField, number> {
  groupName: string;
  ters: Ter[];
}

// The name, workflow, content type and client are set on create and cannot change, so an update
// leaves them out.
export interface TerGroupUpdateRequest extends Record<DefaultTerField, number> {
  currentLiveVersion: number;
  ters: Ter[];
}

const defaultTersOf = (metadata: Metadata): Record<DefaultTerField, number> => ({
  bilingualDefaultTer: Number(metadata.bilingualDefaultTer),
  monolingualDefaultTer: Number(metadata.monolingualDefaultTer),
});

// Blank optional fields are left out of the request.
export const toCreateRequest = (
  groupName: string,
  metadata: Metadata,
  ters: Ter[]
): TerGroupCreateRequest => {
  const request: TerGroupCreateRequest = {
    groupName: groupName.trim(),
    ...defaultTersOf(metadata),
    ters,
  };
  METADATA_FIELDS.forEach(({ field }) => {
    const value = metadata[field].trim();
    if (value !== '') {
      request[field] = value;
    }
  });
  return request;
};

export const toUpdateRequest = (
  currentLiveVersion: number,
  metadata: Metadata,
  ters: Ter[]
): TerGroupUpdateRequest => ({ currentLiveVersion, ...defaultTersOf(metadata), ters });

export const DUPLICATE_NAME_MESSAGE = 'A TER group with this name already exists';

const nameKey = (name: string): string => name.trim().toLowerCase();

/**
 * Whether `name` matches an existing group's name, ignoring case and surrounding spaces. A UI
 * convenience only: the server allows duplicate names, and a concurrent create is not caught.
 */
export const nameTaken = (name: string, groups: TerGroupSummary[]): boolean => {
  const key = nameKey(name);
  return key !== '' && groups.some(group => nameKey(group.groupName || '') === key);
};

export const CONFLICT_HEADER = 'The TER group changed since you loaded it; reload';

const ACCESS_DENIED_MESSAGE = 'You do not have permission to view TER groups.';
const INVALID_VALUE_MESSAGE = 'Invalid value';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);

const entriesOf = (error: ApiError): ApiErrorEntry[] =>
  (error.details && error.details.errors) || [];

/** The create form fields that show their own server errors. */
export type CreateField = 'groupName' | 'workflow';

const CREATE_FIELDS: CreateField[] = ['groupName', 'workflow'];

const isFieldEntry = (entry: ApiErrorEntry, field: CreateField): boolean =>
  (entry.path || [])[0] === field;

/** The error a failed create puts on the group name or workflow field, if any. */
export const fieldErrorOf = (error: ApiError, field: CreateField): string | undefined => {
  if (error.status !== 422) {
    return undefined;
  }
  const entries = entriesOf(error).filter(entry => isFieldEntry(entry, field));
  if (entries.length === 0) {
    return undefined;
  }
  const messages = serverMessages(entries);
  return messages.length > 0 ? messages.join(' ') : INVALID_VALUE_MESSAGE;
};

/**
 * Like a save error, minus the errors fieldErrorOf puts on the name and workflow fields. A new
 * group has no version to conflict with, so a 409 is shown as a form-level error, not a reload
 * alert.
 */
export const describeCreateError = (error: ApiError, rowCount: number): SaveFailure<RowField> => {
  if (error.status === 409) {
    return {
      rowErrors: [],
      formErrors: describeApiError(error, WRITE_DENIED_MESSAGE),
      conflict: null,
    };
  }
  if (CREATE_FIELDS.every(field => fieldErrorOf(error, field) === undefined)) {
    return terRows.describeSaveError(error, rowCount);
  }
  const others = entriesOf(error).filter(
    entry => !CREATE_FIELDS.some(field => isFieldEntry(entry, field))
  );
  if (others.length === 0) {
    return { rowErrors: [], formErrors: [], conflict: null };
  }
  return terRows.describeSaveError({ status: error.status, details: { errors: others } }, rowCount);
};
